<template>
  <div
    v-if="isLoading"
    class="min-vh-100 bg-waves d-flex align-items-center py-1"
  >
    <div class="container w-100 margin-top-min-navbar">
      <loader></loader>
    </div>
  </div>
  <div v-else class="text-start">
    <form
      autocomplete="off"
      id="track-form"
      class="track-form"
      @submit.prevent="onSubmit"
    >
      <sidebar-layout :sidebarSize="4" v-if="track">
        <template v-slot:sidebar>
          <div class="list-group mb-2 d-none d-lg-block">
            <a
              href="#general"
              @click.prevent="scrollTo('general')"
              class="list-group-item list-group-item-action list-group-item-dark"
              aria-current="true"
            >
              General
            </a>
            <a
              href="#sourceAudio"
              @click.prevent="scrollTo('sourceAudio')"
              class="list-group-item list-group-item-action list-group-item-dark"
              >Source Audio</a
            >
            <a
              href="#stems"
              @click.prevent="scrollTo('stems')"
              class="list-group-item list-group-item-action list-group-item-dark"
              >Stems</a
            >

            <a
              href="#permissions"
              @click.prevent="scrollTo('permissions')"
              class="list-group-item list-group-item-action list-group-item-dark"
              >Permissions</a
            >
            <a
              href="#delete"
              @click.prevent="scrollTo('delete')"
              class="list-group-item list-group-item-action list-group-item-dark"
              >Delete</a
            >
          </div>

          <section class="card bg-dark-4 mb-4 text-light">
            <div class="card-footer text-end px-2">
              <div class="d-flex">
                <button
                  type="button"
                  class="btn btn-dark me-1 w-100"
                  @click="cancel()"
                >
                  Cancel
                </button>
                <button
                  class="btn btn-primary ms-1 w-100"
                  type="submit"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting">
                    <span
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span class="visually-hidden">Loading...</span>
                  </span>
                  <span v-else>Save</span>
                </button>
              </div>
            </div>
          </section>
        </template>
        <section class="card bg-dark-4 mb-4 text-light">
          <div class="card-header text-secondary mb-0 text-end border-0">
            <h5 class="mb-0 scroll-m-top-4" ref="general">General</h5>
          </div>
          <div class="card-body">
            <div class="form-group mb-3">
              <label class="required form-label" for="trackFormSongTitle"
                >Song Title</label
              >
              <input
                name="songTitle"
                class="form-control"
                id="trackFormSongTitle"
                required="true"
                v-model="track.songTitle"
                maxlength="100"
              />
            </div>
            <div class="form-group mb-3">
              <label class="form-label" for="trackFormDescription"
                >Description</label
              >
              <textarea
                rows="4"
                name="description"
                type="text"
                class="form-control"
                id="trackFormDescription"
                v-model="track.description"
                maxlength="500"
              />
            </div>
            <!-- <div class="form-group mb-3">
              <label class="form-label" for="trackFormArtistName"
                >Artist/Composer Name(s)</label
              >
              <input
                name="artistName"
                class="form-control"
                id="trackFormArtistName"
                v-model="track.artistName"
                maxlength="100"
              />
            </div> -->
          </div>
        </section>

        <section class="card bg-dark-4 mb-4 text-light">
          <div class="card-header text-secondary mb-0 text-end border-0">
            <h5 class="mb-0 scroll-m-top-4" ref="sourceAudio">Source Audio</h5>
          </div>
          <div class="card-body">
            <file-form-field
              title="Source Audio Track"
              v-model="track.audio"
              accept=".mp3,.wav,.aiff,.flac"
              tooltip="We accept MP3, WAV, AIFF &amp; FLAC under 100 MB and shorter than 10 mins."
              :hasLabel="false"
            ></file-form-field>
          </div>
        </section>

        <section class="card bg-dark-4 mb-4 text-light">
          <div class="card-header text-secondary mb-0 text-end border-0">
            <h5 class="mb-0 scroll-m-top-4" ref="stems">Stems</h5>
          </div>
          <div class="card-body">
            <div v-if="track['split:status']" class="text-center">
              <p
                v-if="track['split:status'] === 'COMPLETED'"
                class="alert alert-success"
                role="alert"
              >
                <i class="bi bi-info-circle-fill"></i> These stems have been
                auto-generated from the source audio
              </p>
              <p
                v-if="track['split:status'] === 'REQUESTED'"
                class="alert alert-success"
                role="alert"
              >
                <i class="bi bi-info-circle-fill"></i> The stems are being
                created.
              </p>
              <div
                v-if="track['split:status'] === 'DEFERRED' || reTryStemSplit"
              >
                <p class="alert alert-info" role="alert">
                  <i class="bi bi-exclamation-triangle-fill"></i> Generation of
                  the stems has been deferred. This is likely due to having
                  reached an account-limit.
                  <input
                    type="checkbox"
                    class="btn-check"
                    id="btn-check"
                    autocomplete="off"
                    @click="reTryStemSplit = !reTryStemSplit"
                    :checked="reTryStemSplit"
                  />
                  <label
                    class="btn btn-dark border-0"
                    :class="reTryStemSplit ? 'bg-success text-dark' : ''"
                    for="btn-check"
                    >Try again</label
                  >
                </p>
              </div>
            </div>
            <file-form-field
              v-if="!reTryStemSplit"
              title="Stems"
              :multiple="true"
              v-model="track.stems"
              :min="2"
              :max="20"
              accept=".mp3,.wav,.aiff,.flac"
              tooltip="We accept MP3, WAV, AIFF &amp; FLAC under 100 MB and shorter than 10 mins"
            ></file-form-field>
          </div>
        </section>

        <section class="card bg-dark-4 mb-4 text-light">
          <div class="card-header text-secondary mb-0 text-end border-0">
            <h5 class="mb-0 scroll-m-top-4" ref="permissions">Permissions</h5>
          </div>
          <div class="card-body">
            <fieldset>
              <div class="form-check form-switch mb-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="checkboxIsPublished"
                  v-model="track.isPublished"
                />
                <label class="form-check-label" for="checkboxIsPublished"
                  >This track is Public / Listed</label
                >
                <tooltip class="ms-2"
                  >This will publish the track thereby make it available to
                  other users</tooltip
                >
              </div>
              <!-- <div class="form-check form-switch mb-3" v-if="track.isPublished">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="checkboxAllowHighQualityMix"
                  v-model="track.allowMixDownload"
                />
                <label
                  class="form-check-label"
                  for="checkboxAllowHighQualityMix"
                  >Allow Download of High Quality Mix
                </label>
              </div>

              <div class="form-check form-switch mb-3" v-if="track.isPublished">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="checkboxAllowMasterDownload"
                  v-model="track.allowMasterDownload"
                />
                <label
                  class="form-check-label"
                  for="checkboxAllowMasterDownload"
                  >Allow Download of Original
                </label>
              </div> -->
            </fieldset>
          </div>
        </section>
        <section class="card bg-dark-4 mb-3 text-light">
          <div class="card-header text-secondary mb-0 text-end border-0">
            <h5 class="mb-0 scroll-m-top-4" ref="delete">Delete</h5>
          </div>
          <div class="card-body text-center mb-4">
            <div>
              <p>
                You can completely delete the track from our systems. Please be
                aware that this is irrevocable.
              </p>
              <button class="btn btn-danger" type="button" @click="deleteTrack">
                Delete this track
              </button>
            </div>
          </div>
        </section>
      </sidebar-layout>
    </form>
  </div>
</template>

<script>
import adminGetTrack from '../services/adminGetTrack';
import Loader from './Loader.vue';
import SidebarLayout from './SidebarLayout.vue';
import adminPutTrack from '../services/adminPutTrack';
import adminDeleteTrack from '../services/adminDeleteTrack';
import FileFormField from './FileFormField.vue';
import Tooltip from './Tooltip.vue';
// import StemPlayerTarget from "./StemPlayerTarget.vue";

export default {
  name: 'AdminEditTrack',
  components: {
    Loader,
    SidebarLayout,
    FileFormField,
    Tooltip,
    // StemPlayerTarget,
  },
  data() {
    return {
      track: undefined,
      isLoading: false,
      isSubmitting: false,
      reTryStemSplit: false,
    };
  },
  async beforeMount() {
    const { trackuuid } = this.$route.params;

    this.track = undefined;

    // this.$player.unset();

    try {
      this.isLoading = true;

      this.track = await adminGetTrack(trackuuid, { cache: false });
    } catch (err) {
      this.$notify(
        {
          title: 'Error',
          text:
            err.response?.status === 404 || err.response?.status === 403
              ? 'This track is no longer available'
              : 'We encountered a problem when loading the track',
          type: 'info',
          group: 'default',
        },
        4000,
      );
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async onSubmit() {
      this.isSubmitting = true;

      const { track } = this;

      try {
        await adminPutTrack(track.trackuuid, track);

        this.$notify(
          {
            title: 'Success',
            text: 'Your changes have been saved',
            type: 'info',
            group: 'default',
          },
          4000,
        );

        this.$router.push({
          name: 'adminViewTrack',
          params: { trackuuid: this.track.trackuuid },
          query: { t: Date.now() },
        });
      } catch (err) {
        this.$notify(
          {
            title: 'Error',
            text: 'Something went wrong. Please try again later.',
            type: 'error',
            group: 'default',
          },
          4000,
        );
        throw err;
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteTrack() {
      if (confirm('Are you sure? (This cannot be undone)')) {
        this.isDeleting = true;

        try {
          await adminDeleteTrack(this.$route.params.trackuuid);

          this.$notify(
            {
              title: 'Info',
              text: 'The track has been deleted. Please note that it may persist for a short period in cached data.',
              type: 'info',
              group: 'default',
            },
            4000,
          );

          this.$router.push({ name: 'adminListTracks' });
        } catch (err) {
          this.$notify(
            {
              title: 'Error',
              text: 'Something went wrong. Please try again later.',
              type: 'error',
              group: 'default',
            },
            4000,
          );
        } finally {
          this.isSubmitting = false;
        }
      }
    },

    cancel() {
      if (confirm('Are you sure? (Any changes will be lost)')) {
        this.$router.push({
          name: 'adminViewTrack',
          params: { trackuuid: this.track.trackuuid },
          query: { t: Date.now() },
        });
      }
    },

    scrollTo(ref) {
      this.$refs[ref].scrollIntoView({ behavior: 'smooth' });
    },
  },

  watch: {
    reTryStemSplit(v) {
      if (v) this.track['split:retry'] = true;
    },
  },
};
</script>

<style scoped></style>
